import {
  Outlet,
  createRootRoute,
  redirect,
  retainSearchParams,
} from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { DOM_ID } from '@/shared/constants/dom'
import { useNotFound, useRootLayout } from './__root.hook'
import { Text, AppHeader } from '@/components'
import { IframeVisibilityProvider } from '@/shared/context/iframe-visibility-context'
import { Button, CloseIcon } from '@/components/atoms'
import { Sidebar, AppUpdateScreen } from '@/components/molecules'
import { Capacitor } from '@capacitor/core'
import { APP_VERSION_VARIANT } from '@/shared/hooks/use-app-version-flag'
import { ROUTES } from '@/shared/constants/routes'

const IFRAME_BASE_CLASS =
  'fixed max-md:top-[calc(var(--modal-mobile-offset)+var(--sat))] md:top-[var(--header-h)] inset-x-0 bottom-0 z-[var(--z-iframe)] overflow-y-auto bg-white rounded-t-2xl pt-8 md:px-8 md:inset-x-auto md:left-auto md:right-0 md:w-[var(--panel-w)] md:rounded-t-none md:rounded-l-xl'
const IFRAME_HIDDEN_CLASS =
  'fixed w-0 h-0 overflow-hidden pointer-events-none -right-[9999px] top-0'

export const Route = createRootRoute({
  validateSearch: (
    search: Record<string, string | boolean>,
  ): { hideHeader?: boolean } => {
    const on = search.hideHeader === true || search.hideHeader === 'true'
    return on ? { hideHeader: true } : {}
  },
  search: {
    middlewares: [retainSearchParams(['hideHeader'])],
  },
  beforeLoad: ({ location }) => {
    const search = location.search as Record<string, string>
    const sessionToken = search.sessionToken
    if (sessionToken && !location.pathname.startsWith('/auth')) {
      const remainingSearch = new URLSearchParams(
        location.search as Record<string, string>,
      )
      remainingSearch.delete('sessionToken')
      const searchStr = remainingSearch.toString()
      const redirectPath =
        location.pathname + (searchStr ? `?${searchStr}` : '')
      throw redirect({
        to: ROUTES.LOGIN,
        search: {
          email: undefined,
          token: undefined,
          sessionToken,
          redirect: redirectPath || undefined,
        },
      })
    }
  },
  component: () => (
    <IframeVisibilityProvider>
      <RootLayout />
    </IframeVisibilityProvider>
  ),
  notFoundComponent: NotFoundPage,
})

function NotFoundPage() {
  const { t } = useTranslation()
  const { isAuthenticated, isLoading } = useNotFound()

  if (isLoading || !isAuthenticated) return <></>

  return (
    <div className="flex min-h-screen items-center justify-center">
      <Text className="text-2xl font-semibold">{t('errors.pageNotFound')}</Text>
    </div>
  )
}

function RootLayout() {
  useNotFound()
  const {
    isAuthPage,
    hideHeader,
    mounted,
    visible,
    handleClose,
    safeAreaTop,
    keyboardHeight,
    appVersionStatus,
    appVersionPayload,
    warnDismissed,
    setWarnDismissed,
  } = useRootLayout()

  const isNative = Capacitor.isNativePlatform()

  if (isNative && appVersionStatus === APP_VERSION_VARIANT.BLOCK) {
    return <AppUpdateScreen payload={appVersionPayload} />
  }

  const iframeClass = mounted
    ? `${IFRAME_BASE_CLASS} ${visible ? 'transition-transform duration-300 translate-y-0 md:translate-x-0' : 'translate-y-full md:translate-x-full md:translate-y-0'}`
    : IFRAME_HIDDEN_CLASS

  return (
    <div className={`flex h-dvh flex-col bg-white`}>
      {!isAuthPage && (
        <div className={iframeClass}>
          <div
            id={DOM_ID.IFRAME_CONTAINER}
            className="rounded-2xl bg-neutral-50 p-3"
          />
          {mounted && (
            <Button
              onClick={handleClose}
              variant="link"
              className={`fixed top-[3.5rem] right-8 z-[var(--z-iframe-close)] rounded-full text-indigo-900 transition-opacity duration-300 md:right-[3.87rem] ${visible ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
              aria-label="Close"
            >
              <CloseIcon className="h-5 w-5" />
            </Button>
          )}
        </div>
      )}
      {!isAuthPage && !hideHeader && <AppHeader />}
      {(isAuthPage || hideHeader) && safeAreaTop > 0 && (
        <div style={{ height: safeAreaTop, flexShrink: 0 }} />
      )}
      <main
        className="flex-1 overflow-auto"
        style={
          keyboardHeight > 0 ? { paddingBottom: keyboardHeight } : undefined
        }
      >
        <Outlet />
      </main>
      {isNative &&
        appVersionStatus === APP_VERSION_VARIANT.WARN &&
        !isAuthPage && (
          <Sidebar
            isOpen={!warnDismissed}
            onClose={() => setWarnDismissed(true)}
            hideCloseButton
          >
            <AppUpdateScreen
              payload={appVersionPayload}
              onDismiss={() => setWarnDismissed(true)}
            />
          </Sidebar>
        )}
    </div>
  )
}
